/**
 * HomeToolbar — barra sticky com search + filter chips + counter.
 *
 * UX Pass 2 E1: escala da listagem da Home pra >10 clientes.
 *
 * - Search: filtra por nome, slug, account_id (substring case-insensitive)
 * - Keybind '/' foca o input (não conflita com ⌘K global)
 * - Filter chips: Todos | Internal | Externos
 * - Counter: "X de Y clientes"
 *
 * Filtro 'Com pending' adiado pra E2 (precisa cruzar requests do user).
 */

import { useEffect, useRef } from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';

import { cn } from '@/lib/utils/cn';

export type ClientTypeFilter = 'all' | 'internal' | 'external';

interface HomeToolbarProps {
  query: string;
  onQueryChange: (q: string) => void;
  typeFilter: ClientTypeFilter;
  onTypeFilterChange: (f: ClientTypeFilter) => void;
  visibleCount: number;
  totalCount: number;
}

const FILTERS: Array<{ value: ClientTypeFilter; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'internal', label: 'Internos' },
  { value: 'external', label: 'Clientes' },
];

export function HomeToolbar({
  query,
  onQueryChange,
  typeFilter,
  onTypeFilterChange,
  visibleCount,
  totalCount,
}: HomeToolbarProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keybind '/' foca search (Linear/GitHub pattern).
  // Ignora se foco já está em input/textarea/contenteditable.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== '/') return;
      const t = e.target as Element | null;
      const tag = t && 'tagName' in t ? t.tagName.toLowerCase() : '';
      const editable = t instanceof HTMLElement && t.isContentEditable;
      if (tag === 'input' || tag === 'textarea' || editable) return;
      e.preventDefault();
      inputRef.current?.focus();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filtered = visibleCount !== totalCount;

  return (
    <div className="mb-4 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
      {/* Search + chips */}
      <div className="flex flex-1 flex-col gap-2.5 sm:flex-row sm:items-center">
        {/* Search */}
        <div className="relative max-w-sm flex-1">
          <MagnifyingGlass
            size={14}
            weight="bold"
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Buscar cliente, conta…"
            aria-label="Buscar cliente ou conta"
            className={cn(
              'h-9 w-full rounded-md border border-border bg-surface pl-8 pr-8',
              'text-[0.8125rem] text-ink shadow-soft transition-[border-color,box-shadow]',
              'focus:border-signal focus:outline-none focus:ring-2 focus:ring-signal/15',
              'placeholder:text-muted',
            )}
          />
          {query && (
            <button
              type="button"
              onClick={() => onQueryChange('')}
              aria-label="Limpar busca"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted hover:bg-surface-muted hover:text-ink"
            >
              <X size={12} weight="bold" aria-hidden="true" />
            </button>
          )}
          {!query && (
            <kbd
              aria-hidden="true"
              className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded-sm border border-border bg-surface-muted px-1 font-mono text-[0.625rem] text-muted sm:block"
            >
              /
            </kbd>
          )}
        </div>

        {/* Type filter chips */}
        <div
          role="group"
          aria-label="Filtrar por tipo"
          className="inline-flex items-center gap-1 rounded-md border border-border bg-surface p-0.5"
        >
          {FILTERS.map((f) => {
            const active = typeFilter === f.value;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => onTypeFilterChange(f.value)}
                aria-pressed={active}
                className={cn(
                  'rounded px-2.5 py-1 font-mono text-[0.6875rem] uppercase tracking-wider transition-colors',
                  active
                    ? 'bg-ink text-surface'
                    : 'text-muted hover:bg-surface-muted hover:text-ink',
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Counter */}
      <p
        aria-live="polite"
        className="font-mono text-[0.6875rem] uppercase tracking-[0.14em] text-muted"
      >
        {filtered ? (
          <>
            <span className="tabular-nums text-ink">{visibleCount}</span> de{' '}
            <span className="tabular-nums">{totalCount}</span>{' '}
            {totalCount === 1 ? 'cliente' : 'clientes'}
          </>
        ) : (
          <>
            <span className="tabular-nums text-ink">{totalCount}</span>{' '}
            {totalCount === 1 ? 'cliente' : 'clientes'}
          </>
        )}
      </p>
    </div>
  );
}
